import getConfig from 'next/config'
import { PaginatedResponse, PaginatedWithItems } from '@modules/pagination-domain/legacy'
import {
    GetNextPageParamFunction,
    MutationCache,
    QueryCache,
} from '@tanstack/react-query'
import { TRPCClientError, httpLink } from '@trpc/client'
import { createTRPCNext } from '@trpc/next'
import { TRPC_ERROR_CODE_KEY } from '@trpc/server/rpc'
import type { AppRouter } from 'server/trpc/routes/_app'
import superjson from 'superjson'
import { isClientSide } from 'lib/env'

const config = getConfig()

export class TrpcErrorChannel {
    private readonly event: string

    constructor(tag: string) {
        this.event = `trpc-error:${tag}`
    }

    dispatch() {
        window.dispatchEvent(new CustomEvent(this.event))
    }

    listen(handler: () => void) {
        window.addEventListener(this.event, handler)
    }

    unlisten(handler: () => void) {
        window.removeEventListener(this.event, handler)
    }
}

const onTrpcError = (error: unknown) => {
    if (isClientSide && error instanceof TRPCClientError && error.data?.errorTag) {
        new TrpcErrorChannel(error.data.errorTag).dispatch()
    }
}

export function getBaseUrl() {
    if (isClientSide) return ''

    return config.publicRuntimeConfig.appUrlAllEnvs
}

export const trpc = createTRPCNext<AppRouter>({
    config({ ctx }) {
        return {
            links: [
                httpLink({
                    /**
                     * If you want to use SSR, you need to use the server's full URL
                     * @link https://trpc.io/docs/ssr
                     **/
                    url: `${getBaseUrl()}/api/trpc`,
                    transformer: superjson,
                }),
            ],
            queryClientConfig: {
                queryCache: new QueryCache({
                    onError: onTrpcError,
                }),
                mutationCache: new MutationCache({
                    onError: onTrpcError,
                }),
            },
        }
    },
    /**
     * @link https://trpc.io/docs/ssr
     **/
    transformer: superjson,
    ssr: false,
})

export const ERROR_CODES: { [k in string]: TRPC_ERROR_CODE_KEY } = {
    BAD_REQUEST: 'BAD_REQUEST',
    UNAUTHORIZED: 'UNAUTHORIZED',
    FORBIDDEN: 'FORBIDDEN',
    NOT_FOUND: 'NOT_FOUND',
    METHOD_NOT_SUPPORTED: 'METHOD_NOT_SUPPORTED',
    TIMEOUT: 'TIMEOUT',
    CONFLICT: 'CONFLICT',
    PRECONDITION_FAILED: 'PRECONDITION_FAILED',
    PAYLOAD_TOO_LARGE: 'PAYLOAD_TOO_LARGE',
    TOO_MANY_REQUESTS: 'TOO_MANY_REQUESTS',
    CLIENT_CLOSED_REQUEST: 'CLIENT_CLOSED_REQUEST',
    INTERNAL_SERVER_ERROR: 'INTERNAL_SERVER_ERROR',
    UNPROCESSABLE_CONTENT: 'UNPROCESSABLE_CONTENT',
} as const

export const getNextPageParam =
    <T>(): GetNextPageParamFunction<
        number | null | undefined,
        PaginatedWithItems<T> | PaginatedResponse<T>
    > =>
    (lastPage, pages) => {
        const totalResult = pages.reduce(
            (acc, page) => acc + page.pagination.pageSize,
            0,
        ) as number
        if (totalResult >= lastPage.pagination.totalCount) {
            return undefined
        } else {
            return lastPage.pagination.pageNumber + 1
        }
    }
